///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { E2eTestUtils } from '../../../testing/e2e-test-utils';

// services
import { Principal } from '../auth/principal.service';
import { UserAuthorities } from '../../services/user/user-authorities';
import { GtthCtxSensitiveHelpService } from './ctx-sensitive-help.service';

// directive
import { FloatingElementPosition, outsideZone, SuiTooltipDirective, TooltipPosition } from '@siklu/sui-lib/infrastructure';

// rxjs
import { fromEvent, of, Subject } from 'rxjs';
import { delay, mergeMap, takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { SuiSvgIconComponent } from '@siklu/sui-lib/components/svg-icon';
import { ButtonsDirectivesModule } from '../directives/buttons-directives/buttons-directives.module';

@Component({
    selector: 'winde-ctx-sensitive-help',
    styleUrls: ['./ctx-sensitive-help.component.scss'],
    templateUrl: './ctx-sensitive-help.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, SuiTooltipDirective, SuiSvgIconComponent, ButtonsDirectivesModule]
})
export class ContextSensitiveHelpComponent implements OnInit, AfterViewInit, OnDestroy {
    public readonly isTooltipsDisabled = E2eTestUtils.isTooltipsDisabled();

    @ViewChild('tooltip', { read: ElementRef }) tooltipElRef: ElementRef;
    @ViewChild(SuiTooltipDirective) tooltipDirectiveRef: SuiTooltipDirective;
    @Input() delay = 0;
    @Input() placement: TooltipPosition = FloatingElementPosition.TOP;
    @Input() container?: string;
    protected originalText: string;
    protected currentText: string;
    protected isFixed = false;
    protected isEditModeActive = false;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();
    private readonly emptyText = 'Not specified';
    private _path: string | null = null;
    private _text: string | null = null;
    private hasAuthority = false;

    constructor(
        private readonly zone: NgZone,
        private readonly principal: Principal,
        private readonly ctxSensitiveHelpService: GtthCtxSensitiveHelpService
    ) {}

    private _isEditable = false;

    get isEditable(): boolean {
        return this._isEditable;
    }

    set isEditable(isEditable: boolean) {
        this._isEditable = isEditable;
    }

    get tooltipTextPath(): string | null {
        return this._path;
    }

    @Input()
    set tooltipTextPath(path: string) {
        this._path = path?.trim() || null;
    }

    get tooltipText(): string | null {
        return this._text;
    }

    @Input()
    set tooltipText(text: string) {
        this._text = text?.trim() || null;
    }

    ngOnInit(): void {
        if (!this.tooltipText && !this.tooltipTextPath) {
            return;
        }

        this.principal.hasAnyAuthority(UserAuthorities.TOOLTIP_EDIT).then((hasAuthority: boolean) => {
            this.hasAuthority = hasAuthority;
        });
    }

    ngAfterViewInit(): void {
        const targetEl: HTMLElement = this.tooltipElRef.nativeElement.firstElementChild;

        if (!targetEl || this.isTooltipsDisabled) {
            return;
        }

        this.handleMouseEvents(targetEl);
        this.tooltipDirectiveRef.hidden.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isFixed = false;
            this.isEditModeActive = false;
        });
    }

    public startEdit() {
        if (this.isEditable) {
            this.originalText = this.currentText.slice();
            this.isEditModeActive = true;
        }
    }

    public saveText() {
        if (this.currentText.trim() !== this.originalText) {
            this.ctxSensitiveHelpService.setText(this.tooltipTextPath, this.currentText).subscribe((editedText: string) => {
                this.tooltipDirectiveRef.hide();
                this.originalText = editedText;
            });
        } else {
            this.tooltipDirectiveRef.hide();
        }
    }

    public hideTooltip() {
        this.tooltipDirectiveRef.hide();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private handleMouseEvents(target: HTMLElement) {
        const mouseenter$ = fromEvent(target, 'mouseenter');
        const mouseleave$ = fromEvent(target, 'mouseleave');

        mouseenter$
            .pipe(
                outsideZone(this.zone),
                mergeMap((event) => of(event).pipe(delay(200), takeUntil(mouseleave$))),
                takeUntil(this.destroy$)
            )
            .subscribe((event: MouseEvent) => {
                this.zone.run(() => {
                    if (this.tooltipText) {
                        this.currentText = this.tooltipText || null;
                    }

                    if (this.tooltipTextPath) {
                        const text = this.ctxSensitiveHelpService.getText(this.tooltipTextPath);
                        this.isEditable = this.hasAuthority;
                        this.currentText = text || (this.hasAuthority ? this.emptyText : null);
                    }

                    if (this.currentText && !this.tooltipDirectiveRef.isOpen) {
                        this.isFixed = this.isEditable && event.ctrlKey;
                        this.tooltipDirectiveRef.show();
                    }
                });
            });

        mouseleave$.pipe(outsideZone(this.zone), takeUntil(this.destroy$)).subscribe(() => {
            this.zone.run(() => {
                if (this.tooltipDirectiveRef.isOpen && !this.isFixed) {
                    this.tooltipDirectiveRef.hide();
                }
            });
        });
    }
}
